import { Combobox } from '@headlessui/react'
import { clsx } from 'clsx'

import {
  ExternalSearchResult,
  Space,
  Member,
  Post,
} from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { ActionItem } from '../../../CommandBar/CommandBar.types.js'
import { useI18n } from '../../../i18n/index.js'
import {
  ExtendedSmartSearchEntity,
  HistoryItem,
  useSearchHistory,
} from '../../index.js'
import { ExtendedSmartSearchEntityType } from '../../SmartSearch.types.js'

import { SearchEntityRowContent } from './components/SearchEntityRowContent.js'
import { SearchEntityRowHeading } from './components/SearchEntityRowHeading.js'
import { SearchEntityRowTrailing } from './components/SearchEntityRowTrailing.js'

interface Props {
  item: ExtendedSmartSearchEntity
  type: ExtendedSmartSearchEntityType
}

export const SearchEntityRow = ({ item, type }: Props) => {
  const { Link } = useRouter()
  const { removeItem } = useSearchHistory()
  const isPosts =
    type === ExtendedSmartSearchEntityType.POSTS ||
    type === ExtendedSmartSearchEntityType.OFFICIAL_POSTS

  const { $t } = useI18n()

  const handleRemoveFromHistory = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    // Avoid the default behavior of the link to navigate to the item
    e.preventDefault()
    removeItem(item as HistoryItem)
  }

  const { submitItem } = useSearchHistory()

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // prevent combobox onChange, this fixes ctrl/cmd+click to open new tab
    e.stopPropagation()

    submitItem(item, type)
  }

  return (
    <Combobox.Option
      value={
        {
          item,
          entityType: type,
          url:
            (item as Post | Member | Space)?.relativeUrl ||
            (item as ExternalSearchResult)?.url ||
            (item as HistoryItem).data.url,
        } satisfies ActionItem
      }
      className={({ active }) =>
        clsx(
          'flex items-center px-3 rounded-md select-none gap-4 py-2 w-full',
          {
            'bg-surface-subdued cursor-pointer': active,
          },
        )
      }
    >
      <Link
        onClick={handleLinkClick}
        href={
          type === ExtendedSmartSearchEntityType.EXTERNALS ||
          type === ExtendedSmartSearchEntityType.HISTORY
            ? (item as ExternalSearchResult).url ||
              (item as HistoryItem).data.url
            : (item as Post | Member | Space)?.relativeUrl
        }
        className={clsx('flex gap-4 flex-1', {
          'min-h-12': type === ExtendedSmartSearchEntityType.HISTORY || isPosts,
        })}
      >
        <div className="flex sm:items-center justify-center">
          <SearchEntityRowHeading item={item} entityType={type} />
        </div>
        <div className="flex-1 grid grid-cols-8 w-full">
          <div className="col-span-full sm:col-span-6">
            <SearchEntityRowContent item={item} entityType={type} />
          </div>
          <div
            className={clsx('col-span-full sm:col-span-2', {
              hidden: !isPosts,
            })}
          >
            <SearchEntityRowTrailing item={item} entityType={type} />
          </div>
        </div>
        {type === ExtendedSmartSearchEntityType.HISTORY && (
          <IconButton
            variant="plainNeutral"
            size="xs"
            icon={<SvgIcon name="close" />}
            accessibilityLabel={$t({
              id: 'SmartSearch.History.RemoveFromHistory',
              defaultMessage: 'Remove from history',
            })}
            className="self-center"
            onClick={handleRemoveFromHistory}
          />
        )}
      </Link>
    </Combobox.Option>
  )
}
